import * as React from "react";
import { useState, useEffect } from "react";
import { Typography, Card, TextField } from "@mui/material";
import { ClipLoader } from "react-spinners";
import axios from "axios";
import { CustomButton } from "../components/CustomButton";
import { styles } from "../theme/styles";

/**
 * Edición del mínimo de pedido de un proveedor.
 *
 * El mínimo SIEMPRE vive por tienda (StoreModel.providers es un
 * Map<idProveedor, monto>) — no existe un "mínimo único" real en el
 * schema. Este modal se autogestiona: al abrir, pide las tiendas del
 * proveedor (GET /providers/getStoresForProvider/:idNumber) y al guardar
 * manda todo junto (PATCH /providers/updateStoreMinimos).
 *
 * El botón "Aplicar a todas" es solo una ayuda visual: rellena los campos
 * localmente con el mismo monto, pero sigue guardándose por tienda al
 * darle Guardar — no es un modo de datos aparte.
 */
export default function EditarMinimosModal({ open, provider, token, onClose }) {
  const [tiendas, setTiendas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [montoParaTodas, setMontoParaTodas] = useState("");

  useEffect(() => {
    if (!open || !provider) return;

    const cargarTiendas = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_PETCOZOO}/providers/getStoresForProvider/${provider.idNumber}`,
          {
            headers: {
              "Content-Type": "application/json",
              authorization: token,
            },
          },
        );
        const data = res.data?.data || [];
        setTiendas(
          data.map((t) => ({
            ...t,
            valor:
              t.minimo !== null && t.minimo !== undefined
                ? String(t.minimo)
                : "",
          })),
        );
      } catch (error) {
        console.error("Error cargando tiendas del proveedor:", error);
        setTiendas([]);
      } finally {
        setLoading(false);
      }
    };

    cargarTiendas();
    setMontoParaTodas("");
  }, [open, provider]);

  if (!open || !provider) return null;

  const handleValorTienda = (storeIdNumber, valor) => {
    setTiendas((prev) =>
      prev.map((t) => (t.idNumber === storeIdNumber ? { ...t, valor } : t)),
    );
  };

  const handleAplicarATodas = () => {
    if (!montoParaTodas) return;
    setTiendas((prev) => prev.map((t) => ({ ...t, valor: montoParaTodas })));
  };

  const handleGuardar = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_PETCOZOO}/providers/updateStoreMinimos`,
        {
          idNumber: provider.idNumber,
          minimos: tiendas.map((t) => ({
            storeIdNumber: t.idNumber,
            monto: t.valor,
          })),
        },
        {
          headers: {
            "Content-Type": "application/json",
            authorization: token,
          },
        },
      );
      onClose();
    } catch (error) {
      console.error("Error guardando mínimos:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 1100,
        backgroundColor: "rgba(58, 58, 58, 0.8)",
      }}>
      <Card className="div-detalle-prov edicion">
        <Typography variant="h5" color="primary" style={styles.textin}>
          Editar mínimo de pedido
        </Typography>
        <Typography
          variant="body1"
          color="textSecondary"
          sx={{ margin: "1.25rem" }}>
          {provider.name || provider.nameProvider} · ID {provider.idNumber}
        </Typography>

        {loading && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "2rem 0",
            }}>
            <ClipLoader color="#00205B" size="40px" />
          </div>
        )}

        {!loading && tiendas.length === 0 && (
          <p style={{ fontSize: "0.9rem", color: "#8B93A1" }}>
            Este proveedor no tiene tiendas asociadas todavía (revisa que tenga
            al menos un cluster activo).
          </p>
        )}

        {!loading && tiendas.length > 0 && (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",

                gap: "8px",
                marginBottom: "1rem",
              }}>
              <TextField
                label="Aplicar a todas"
                size="small"
                type="number"
                placeholder="$0.00"
                value={montoParaTodas}
                onChange={(e) => setMontoParaTodas(e.target.value)}
              />
              <CustomButton
                className="btn-add-prov btn-cancelar"
                onClick={handleAplicarATodas}>
                Aplicar
              </CustomButton>
            </div>

            <div className="min-list">
              {tiendas.map((t) => (
                <div key={t.idNumber} className="min-list-row edit">
                  <span>{t.storeName}</span>
                  <TextField
                    type="number"
                    size="small"
                    placeholder="Sin definir"
                    value={t.valor}
                    onChange={(e) =>
                      handleValorTienda(t.idNumber, e.target.value)
                    }
                    sx={{ width: "120px" }}
                  />
                </div>
              ))}
            </div>
          </>
        )}

        <div className="div-adm-btn">
          <CustomButton
            className="btn-add-prov btn-guardar"
            disabled={saving || loading}
            onClick={handleGuardar}>
            {saving ? "Guardando..." : "Guardar cambios"}
          </CustomButton>
          <CustomButton className="btn-add-prov btn-cancelar" onClick={onClose}>
            Cancelar
          </CustomButton>
        </div>
      </Card>
    </div>
  );
}
